import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MoreVertical, Bot, Loader2, FileText, ChevronDown, X,
  UserPlus, Users, Check, Tag, ArrowLeft, ChevronRight,
  Contact, BookmarkPlus,
} from "lucide-react";
import { generateChatSummary } from "../../apis/chat";
import { getLivechatAccessInfo, getAvailableAgents, assignChat, updateConversationStatus } from "../../apis/livechat";
import { useLiveChat } from "../../contexts/LiveChatContext";
import ChatSummaryModal from "./ChatSummaryModal";
import { toast } from "react-toastify";

/**
 * ChatHeader — squared WhatsApp-style.
 * Accepts onMobileBack so the parent can hide the chat window on small screens.
 */
const ChatHeader = ({
  selectedChat,
  isWindowOpen,
  chatAiEnabled,
  aiToggleLoading,
  onToggleChatAi,
  onChatAssigned,
  onMobileBack,
  onViewContact,
  onSaveContact,
}) => {
  const { chatStatusesList, updateChatInList } = useLiveChat();

  const [showSummaryDropdown, setShowSummaryDropdown] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [messageCount, setMessageCount] = useState(50);

  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  /** Bottom sheet drill-down (<768): root | status | assign | summary */
  const [mobileSubView, setMobileSubView] = useState("root");

  const [isNarrowViewport, setIsNarrowViewport] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsNarrowViewport(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const { role } = getLivechatAccessInfo();
  const isSuperAdmin = role === "super_admin";
  const messageCountOptions = [10, 25, 50, 100, 200, 500];

  const hasSavedContact = !!selectedChat.is_saved_contact;
  const handleContactAction = () => {
    closeAll();
    if (hasSavedContact) onViewContact?.();
    else onSaveContact?.();
  };

  /* ─── Avatar ─── */
  const getInitials = (name = "") => name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const AVATAR_COLORS = ["#25D366","#128C7E","#3b82f6","#8b5cf6","#f59e0b","#ec4899","#14b8a6","#6366f1"];
  const getAvatarColor = (str = "") => AVATAR_COLORS[str.charCodeAt(0) % AVATAR_COLORS.length];

  useEffect(() => {
    if (showAssignDropdown && isSuperAdmin) fetchAvailableAgents();
  }, [showAssignDropdown, isSuperAdmin]);

  useEffect(() => {
    if (
      showMoreMenu &&
      mobileSubView === "assign" &&
      isSuperAdmin &&
      isNarrowViewport
    ) {
      fetchAvailableAgents();
    }
  }, [showMoreMenu, mobileSubView, isSuperAdmin, isNarrowViewport]);

  const fetchAvailableAgents = async () => {
    setLoadingAgents(true);
    try {
      const res = await getAvailableAgents();
      if (res.data.success) setAvailableAgents(res.data.data || []);
    } catch {
      toast.error("Failed to load agents");
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleAssignChat = async (agentId) => {
    if (!selectedChat || assigning) return;
    setAssigning(true);
    try {
      const res = await assignChat(selectedChat.customer_phone, selectedChat.phone_number_id, agentId);
      if (res.data.success) {
        const agentName = agentId ? availableAgents.find((a) => a.id === agentId)?.name || "Agent" : null;
        toast.success(agentId ? `Assigned to ${agentName}` : "Chat unassigned");
        setShowAssignDropdown(false);
        onChatAssigned?.(res.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to assign chat");
    } finally {
      setAssigning(false);
    }
  };

  const handleStatusChange = async (statusId) => {
    if (!selectedChat || statusUpdating) return;
    setStatusUpdating(true);
    setShowStatusDropdown(false);
    try {
      const res = await updateConversationStatus(selectedChat.customer_phone, selectedChat.phone_number_id, statusId);
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        updateChatInList(selectedChat.id, {
          status_id: d.status_id, status_name: d.status_name,
          status_color: d.status_color, status_updated_at: d.status_updated_at,
        });
        toast.success("Status updated");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update status");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedChat || generatingSummary) return;
    setGeneratingSummary(true);
    setShowSummaryDropdown(false);
    try {
      const res = await generateChatSummary(selectedChat.customer_phone, selectedChat.phone_number_id, messageCount);
      if (res.data.success) { setSummaryData(res.data.data); setShowSummaryModal(true); }
      else alert("Failed to generate summary.");
    } catch {
      alert("Failed to generate summary.");
    } finally {
      setGeneratingSummary(false);
    }
  };

  const closeAll = () => {
    setShowSummaryDropdown(false);
    setShowAssignDropdown(false);
    setShowStatusDropdown(false);
    setShowMoreMenu(false);
    setMobileSubView("root");
  };

  return (
    <>
      <div className="ch-root">
        {/* Left: back + avatar + info */}
        <div className="ch-left">
          {/* Mobile back button */}
          {onMobileBack && (
            <button className="ch-back-btn" onClick={onMobileBack} aria-label="Back">
              <ArrowLeft size={18} />
            </button>
          )}

          <div className="ch-avatar-wrap">
            <div
              className="ch-avatar"
              style={{ background: getAvatarColor(selectedChat.name) }}
              aria-hidden="true"
            >
              {getInitials(selectedChat.name)}
            </div>
            {!isWindowOpen && <span className="ch-window-dot" title="Window closed" />}
          </div>

          <div className="ch-info">
            <button
              type="button"
              className="ch-name ch-name-btn"
              onClick={handleContactAction}
              title={hasSavedContact ? "View contact" : "Save contact"}
            >
              {selectedChat.name}
            </button>
            <div className="ch-meta">
              <span className="ch-phone">{selectedChat.phone}</span>
              <span className="ch-meta-badges">
                {selectedChat.assigned_agent_name && (
                  <span className="ch-agent-chip">
                    <Users size={9} />{selectedChat.assigned_agent_name}
                  </span>
                )}
                {!isWindowOpen && (
                  <span className="ch-window-pill">Window closed</span>
                )}
                <span
                  className="ch-status-pill"
                  style={{
                    background: (selectedChat.status_color || "#9ca3af") + "22",
                    color: selectedChat.status_color || "#6b7280",
                  }}
                >
                  {selectedChat.status_name || "No status"}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Right: action buttons (desktop/tablet — hidden on phones) */}
        <div className="ch-right ch-right--desktop">
          {/* View / Save contact */}
          <button
            onClick={handleContactAction}
            className={`ch-action-btn${hasSavedContact ? "" : " ch-action-btn--save"}`}
            title={hasSavedContact ? "View contact details" : "Save this contact"}
          >
            {hasSavedContact ? <Contact size={13} /> : <BookmarkPlus size={13} />}
            <span className="ch-btn-label">{hasSavedContact ? "Contact" : "Save contact"}</span>
          </button>

          {/* Status dropdown */}
          <div className="ch-dd-wrap">
            <button
              onClick={() => { closeAll(); setShowStatusDropdown(!showStatusDropdown); }}
              disabled={statusUpdating}
              className="ch-action-btn"
              title="Change status"
            >
              {statusUpdating ? <Loader2 size={13} className="ch-spin" /> : <Tag size={13} />}
              <span className="ch-btn-label">{selectedChat.status_name || "Status"}</span>
              <ChevronDown size={11} />
            </button>
            {showStatusDropdown && (
              <>
                <div className="ch-overlay" onClick={closeAll} />
                <div className="ch-dropdown ch-dropdown--right">
                  <div className="ch-dd-header">Set status</div>
                  <button onClick={() => handleStatusChange(null)} className="ch-dd-row ch-dd-row--clear">
                    Clear status
                  </button>
                  {chatStatusesList.map((s) => (
                    <button key={s.id} onClick={() => handleStatusChange(s.id)} className="ch-dd-row">
                      <span className="ch-status-dot" style={{ background: s.color || "#9ca3af" }} />
                      <span className="ch-dd-row-label">{s.name}</span>
                      {selectedChat.status_id === s.id && <Check size={13} className="ch-check-icon" />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Assign (super admin) */}
          {isSuperAdmin && (
            <div className="ch-dd-wrap">
              <button
                onClick={() => { closeAll(); setShowAssignDropdown(!showAssignDropdown); }}
                disabled={assigning}
                className={`ch-action-btn${selectedChat.assigned_agent_id ? " ch-action-btn--blue" : ""}`}
                title={selectedChat.assigned_agent_id ? "Reassign" : "Assign"}
              >
                {assigning ? <Loader2 size={13} className="ch-spin" /> : <UserPlus size={13} />}
                <span className="ch-btn-label">{selectedChat.assigned_agent_id ? "Reassign" : "Assign"}</span>
                <ChevronDown size={11} />
              </button>
              {showAssignDropdown && (
                <>
                  <div className="ch-overlay" onClick={closeAll} />
                  <div className="ch-dropdown ch-dropdown--right">
                    <div className="ch-dd-header">Assign to agent</div>
                    {loadingAgents ? (
                      <div className="ch-dd-loader"><Loader2 size={16} className="ch-spin" /></div>
                    ) : availableAgents.length === 0 ? (
                      <div className="ch-dd-empty">No agents available</div>
                    ) : (
                      <div className="ch-dd-list">
                        {selectedChat.assigned_agent_id && (
                          <button onClick={() => handleAssignChat(null)} disabled={assigning} className="ch-dd-row ch-dd-row--danger">
                            <X size={13} />
                            <span className="ch-dd-row-label">Unassign</span>
                          </button>
                        )}
                        {availableAgents.map((agent) => (
                          <button
                            key={agent.id}
                            onClick={() => handleAssignChat(agent.id)}
                            disabled={assigning || agent.id === selectedChat.assigned_agent_id}
                            className={`ch-dd-row${agent.id === selectedChat.assigned_agent_id ? " ch-dd-row--active" : ""}`}
                          >
                            <div className="ch-agent-avatar" style={{ background: getAvatarColor(agent.name) }}>
                              {getInitials(agent.name)}
                            </div>
                            <div className="ch-agent-info">
                              <span className="ch-agent-name">{agent.name}</span>
                              <span className="ch-agent-email">{agent.email}</span>
                            </div>
                            {agent.id === selectedChat.assigned_agent_id && <Check size={13} className="ch-check-icon" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Generate Summary */}
          <div className="ch-dd-wrap">
            <button
              onClick={() => { closeAll(); setShowSummaryDropdown(!showSummaryDropdown); }}
              disabled={generatingSummary}
              className="ch-action-btn ch-action-btn--blue"
              title="Generate summary"
            >
              {generatingSummary ? <Loader2 size={13} className="ch-spin" /> : <FileText size={13} />}
              <span className="ch-btn-label">{generatingSummary ? "Generating…" : "Summary"}</span>
              {!generatingSummary && <ChevronDown size={11} />}
            </button>
            {showSummaryDropdown && !generatingSummary && (
              <>
                <div className="ch-overlay" onClick={closeAll} />
                <div className="ch-dropdown ch-dropdown--right" style={{ minWidth: 200 }}>
                  <div className="ch-dd-header">Last N messages</div>
                  <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                    <select
                      value={messageCount}
                      onChange={(e) => setMessageCount(parseInt(e.target.value, 10))}
                      className="ch-select"
                    >
                      {messageCountOptions.map((n) => (
                        <option key={n} value={n}>{n} messages</option>
                      ))}
                    </select>
                    <button onClick={handleGenerateSummary} className="ch-generate-btn">
                      Generate
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

        {/* AI toggle */}
          <button
            onClick={onToggleChatAi}
            disabled={aiToggleLoading}
            className={`ch-ai-btn${chatAiEnabled ? " ch-ai-btn--on" : ""}`}
            title={chatAiEnabled ? "AI On — click to disable" : "AI Off — click to enable"}
          >
            {aiToggleLoading ? <Loader2 size={13} className="ch-spin" /> : <Bot size={13} />}
            <span className="ch-btn-label">{chatAiEnabled ? "AI On" : "AI Off"}</span>
          </button>
        </div>

        {/* Phones: WhatsApp-like overflow — one menu opens action sheet */}
        <div className="ch-right ch-right--mobile">
          <button
            type="button"
            className="ch-mobile-menu-trigger"
            onClick={() => {
              setShowSummaryDropdown(false);
              setShowAssignDropdown(false);
              setShowStatusDropdown(false);
              const open = !showMoreMenu;
              setShowMoreMenu(open);
              if (open) setMobileSubView("root");
              else setMobileSubView("root");
            }}
            aria-label="Conversation menu"
            aria-expanded={showMoreMenu}
          >
            <MoreVertical size={20} strokeWidth={2} />
          </button>
        </div>
      </div>

      {showMoreMenu &&
        isNarrowViewport &&
        typeof document !== "undefined" &&
        createPortal(
          <>
            <div className="ch-ms-backdrop" onClick={closeAll} aria-hidden="true" />
            <div className="ch-ms-sheet" role="dialog" aria-label="Conversation actions">
              <div className="ch-ms-handle-wrap" aria-hidden="true"><div className="ch-ms-handle" /></div>

              {mobileSubView !== "root" && (
                <div className="ch-ms-toolbar">
                  <button
                    type="button"
                    className="ch-ms-back"
                    onClick={() => setMobileSubView("root")}
                    aria-label="Back"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <span className="ch-ms-toolbar-title">
                    {mobileSubView === "status" && "Conversation status"}
                    {mobileSubView === "assign" && "Assign to agent"}
                    {mobileSubView === "summary" && "Chat summary"}
                  </span>
                </div>
              )}

              {mobileSubView === "root" && (
                <div className="ch-ms-root">
                  <div className="ch-ms-sheet-title-row">
                    <span className="ch-ms-sheet-title">Conversation tools</span>
                    <button type="button" className="ch-ms-close-x" onClick={closeAll} aria-label="Close menu">
                      <X size={18} />
                    </button>
                  </div>

                  <button
                    type="button"
                    className="ch-ms-row"
                    onClick={handleContactAction}
                  >
                    {hasSavedContact
                      ? <Contact size={18} className="ch-ms-row-icon" />
                      : <BookmarkPlus size={18} className="ch-ms-row-icon" />}
                    <span className="ch-ms-row-text">
                      {hasSavedContact ? "View contact" : "Save contact"}
                    </span>
                    <ChevronRight size={16} className="ch-ms-row-chevron" />
                  </button>

                  <button
                    type="button"
                    className="ch-ms-row"
                    onClick={() => setMobileSubView("status")}
                    disabled={statusUpdating}
                  >
                    <Tag size={18} className="ch-ms-row-icon" />
                    <span className="ch-ms-row-text">Change status</span>
                    <span className="ch-ms-row-hint">{selectedChat.status_name || "None"}</span>
                    <ChevronRight size={16} className="ch-ms-row-chevron" />
                  </button>

                  {isSuperAdmin && (
                    <button type="button" className="ch-ms-row" onClick={() => setMobileSubView("assign")}>
                      <UserPlus size={18} className="ch-ms-row-icon" />
                      <span className="ch-ms-row-text">Assign conversation</span>
                      <ChevronRight size={16} className="ch-ms-row-chevron" />
                    </button>
                  )}

                  <button
                    type="button"
                    className="ch-ms-row"
                    onClick={() => setMobileSubView("summary")}
                    disabled={generatingSummary}
                  >
                    <FileText size={18} className="ch-ms-row-icon" />
                    <span className="ch-ms-row-text">Summary</span>
                    <ChevronRight size={16} className="ch-ms-row-chevron" />
                  </button>

                  <button
                    type="button"
                    className="ch-ms-row ch-ms-row--toggle"
                    onClick={() => onToggleChatAi?.()}
                    disabled={aiToggleLoading}
                  >
                    <Bot size={18} className="ch-ms-row-icon" />
                    <span className="ch-ms-row-text">AI auto-replies</span>
                    <span className={`ch-ms-pill${chatAiEnabled ? " ch-ms-pill--on" : ""}`}>
                      {aiToggleLoading ? "…" : chatAiEnabled ? "On" : "Off"}
                    </span>
                  </button>
                </div>
              )}

              {mobileSubView === "status" && (
                <div className="ch-ms-scroll">
                  <button
                    type="button"
                    className="ch-ms-chip-row"
                    onClick={async () => {
                      await handleStatusChange(null);
                      closeAll();
                    }}
                  >
                    Clear status
                  </button>
                  {chatStatusesList.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`ch-ms-chip-row${selectedChat.status_id === s.id ? " ch-ms-chip-row--active" : ""}`}
                      onClick={async () => {
                        await handleStatusChange(s.id);
                        closeAll();
                      }}
                    >
                      <span className="ch-status-dot" style={{ background: s.color || "#9ca3af" }} />
                      <span className="ch-ms-chip-label">{s.name}</span>
                      {selectedChat.status_id === s.id && <Check size={16} className="ch-check-icon" />}
                    </button>
                  ))}
                </div>
              )}

              {mobileSubView === "assign" && isSuperAdmin && (
                <div className="ch-ms-scroll">
                  {loadingAgents ? (
                    <div className="ch-dd-loader"><Loader2 size={20} className="ch-spin" /></div>
                  ) : availableAgents.length === 0 ? (
                    <div className="ch-dd-empty">No agents available</div>
                  ) : (
                    <>
                      {selectedChat.assigned_agent_id && (
                        <button
                          type="button"
                          className="ch-ms-chip-row ch-dd-row--danger"
                          onClick={async () => {
                            await handleAssignChat(null);
                            closeAll();
                          }}
                          disabled={assigning}
                        >
                          Unassign
                        </button>
                      )}
                      {availableAgents.map((agent) => (
                        <button
                          key={agent.id}
                          type="button"
                          className={`ch-ms-agent-row${agent.id === selectedChat.assigned_agent_id ? " ch-ms-agent-row--active" : ""}`}
                          disabled={assigning || agent.id === selectedChat.assigned_agent_id}
                          onClick={async () => {
                            await handleAssignChat(agent.id);
                            closeAll();
                          }}
                        >
                          <div className="ch-agent-avatar" style={{ background: getAvatarColor(agent.name) }}>
                            {getInitials(agent.name)}
                          </div>
                          <div className="ch-agent-info">
                            <span className="ch-agent-name">{agent.name}</span>
                            <span className="ch-agent-email">{agent.email}</span>
                          </div>
                          {agent.id === selectedChat.assigned_agent_id && <Check size={16} className="ch-check-icon" />}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}

              {mobileSubView === "summary" && (
                <div className="ch-ms-summary">
                  <p className="ch-ms-summary-hint">Include the last N messages in the summary.</p>
                  <select
                    value={messageCount}
                    onChange={(e) => setMessageCount(parseInt(e.target.value, 10))}
                    className="ch-select"
                  >
                    {messageCountOptions.map((n) => (
                      <option key={n} value={n}>{n} messages</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={async () => {
                      await handleGenerateSummary();
                      closeAll();
                    }}
                    className="ch-generate-btn"
                    disabled={generatingSummary}
                  >
                    {generatingSummary ? "Generating…" : "Generate summary"}
                  </button>
                </div>
              )}
            </div>
          </>,
          document.body
        )}

      {/* Summary Modal */}
      {showSummaryModal && summaryData && (
        <ChatSummaryModal
          summaryData={summaryData}
          onClose={() => { setShowSummaryModal(false); setSummaryData(null); }}
        />
      )}

      {/* Desktop dropdown overlays only (mobile sheet uses its own backdrop). */}
      {(showSummaryDropdown || showAssignDropdown || showStatusDropdown) && (
        <div className="ch-overlay" onClick={closeAll} />
      )}

      <style>{`
        .ch-root {
          --ch-green: #25D366;
          --ch-green-dk: #128C7E;
          --ch-border: rgba(0,0,0,0.09);
          --ch-text: #111b21;
          --ch-text-2: #54656f;
          --ch-text-3: #8696a0;
          --ch-surface: #f0f2f5;
          --ch-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 10px 14px;
          background: var(--ch-surface);
          border-bottom: 0.5px solid var(--ch-border);
          flex-shrink: 0;
          font-family: 'Segoe UI', system-ui, sans-serif;
        }

        /* Left */
        .ch-left { display: flex; align-items: center; gap: 10px; min-width: 0; flex: 1; }
        .ch-back-btn {
          display: none;
          width: 32px; height: 32px; border-radius: var(--ch-radius);
          border: none; background: none; color: var(--ch-text-2); cursor: pointer;
          align-items: center; justify-content: center; flex-shrink: 0;
        }
        .ch-back-btn:hover { background: rgba(0,0,0,0.06); }

        @media (max-width: 767px) {
          .ch-back-btn { display: flex; }
        }

        /* Avatar */
        .ch-avatar-wrap { position: relative; flex-shrink: 0; }
        .ch-avatar {
          width: 38px; height: 38px; border-radius: var(--ch-radius);
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700; color: #fff; letter-spacing: 0.5px;
        }
        .ch-window-dot {
          position: absolute; top: -3px; right: -3px; width: 8px; height: 8px;
          border-radius: 50%; background: #f59e0b; border: 2px solid var(--ch-surface);
        }

        /* Info */
        .ch-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .ch-name { font-size: 14px; font-weight: 600; color: var(--ch-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ch-name-btn {
          border: none; background: none; padding: 0; margin: 0; cursor: pointer;
          text-align: left; max-width: 100%; font-family: inherit;
        }
        .ch-name-btn:hover { color: var(--ch-green-dk); text-decoration: underline; }
        .ch-meta {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
          min-width: 0;
          width: 100%;
        }
        @media (min-width: 768px) {
          .ch-meta { flex-direction: row; flex-wrap: wrap; align-items: center; gap: 5px; }
        }
        .ch-phone { font-size: 11px; color: var(--ch-text-3); max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ch-meta-badges { display: flex; flex-wrap: wrap; gap: 4px 6px; align-items: center; max-width: 100%; }
        .ch-agent-chip {
          display: inline-flex; align-items: center; gap: 3px; font-size: 10px; color: #1d4ed8; background: #dbeafe;
          padding: 1px 5px; border-radius: 3px; font-weight: 500;
          max-width: 220px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .ch-window-pill { font-size: 10px; font-weight: 600; padding: 1px 6px; border-radius: 3px; background: #fef3c7; color: #92400e; }
        .ch-status-pill { font-size: 10px; font-weight: 500; padding: 1px 5px; border-radius: 3px; max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; vertical-align: middle; }

        @media (max-width: 767px) {
          .ch-meta-badges { display: none; }
          .ch-root {
            padding: 8px 8px 8px 4px;
            gap: 6px;
            margin-top: 8px; /* breathing room on phones */
          }
          .ch-avatar-wrap .ch-avatar { width: 34px; height: 34px; font-size: 12px; }
        }

        /* Toolbar layout: desktop dropdowns / mobile overflow */
        @media (min-width: 768px) {
          .ch-right--mobile { display: none !important; }
        }
        @media (max-width: 767px) {
          .ch-right--desktop { display: none !important; }
        }
        .ch-mobile-menu-trigger {
          width: 40px;
          height: 40px;
          border-radius: var(--ch-radius);
          border: none;
          background: transparent;
          color: var(--ch-text);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          -webkit-tap-highlight-color: transparent;
        }
        .ch-mobile-menu-trigger:active {
          background: rgba(0,0,0,0.06);
        }

        /* Right */
        .ch-right { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
        .ch-dd-wrap { position: relative; }

        /* Action buttons */
        .ch-action-btn {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 5px 10px; border-radius: var(--ch-radius); border: 0.5px solid var(--ch-border);
          background: #fff; color: var(--ch-text-2); font-size: 11.5px; font-weight: 500;
          cursor: pointer; transition: all 0.15s; white-space: nowrap;
        }
        .ch-action-btn:hover:not(:disabled) { background: rgba(0,0,0,0.04); }
        .ch-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ch-action-btn--blue { color: #1d4ed8; background: #eff6ff; border-color: #bfdbfe; }
        .ch-action-btn--blue:hover:not(:disabled) { background: #dbeafe; }
        .ch-action-btn--save { color: var(--ch-green-dk); background: rgba(37,211,102,0.1); border-color: rgba(37,211,102,0.3); }
        .ch-action-btn--save:hover:not(:disabled) { background: rgba(37,211,102,0.18); }
        .ch-btn-label { display: none; }

        @media (min-width: 900px) {
          .ch-btn-label { display: inline; }
        }

        /* AI button */
        .ch-ai-btn {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 5px 10px; border-radius: var(--ch-radius); border: 0.5px solid var(--ch-border);
          background: #fff; color: var(--ch-text-2); font-size: 11.5px; font-weight: 500;
          cursor: pointer; transition: all 0.15s; white-space: nowrap;
        }
        .ch-ai-btn:hover:not(:disabled) { background: rgba(0,0,0,0.04); }
        .ch-ai-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ch-ai-btn--on { color: var(--ch-green-dk); background: rgba(37,211,102,0.1); border-color: rgba(37,211,102,0.3); }
        .ch-ai-btn--on:hover:not(:disabled) { background: rgba(37,211,102,0.16); }

        .ch-more-btn {
          width: 32px; height: 32px; border-radius: var(--ch-radius); border: none;
          background: none; color: var(--ch-text-2); cursor: pointer;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
          transition: background 0.15s;
        }
        .ch-more-btn:hover { background: rgba(0,0,0,0.06); }

        /* Dropdown */
        .ch-overlay { position: fixed; inset: 0; z-index: 40; }
        .ch-dropdown {
          position: absolute; top: calc(100% + 4px); z-index: 50;
          background: #fff; border: 0.5px solid var(--ch-border); border-radius: var(--ch-radius);
          box-shadow: 0 4px 16px rgba(0,0,0,0.1); min-width: 210px; max-height: 300px; overflow: hidden;
        }
        .ch-dropdown--right { right: 0; }
        .ch-dd-header { padding: 8px 12px; font-size: 11px; font-weight: 600; color: var(--ch-text-3); border-bottom: 0.5px solid var(--ch-border); background: var(--ch-surface); text-transform: uppercase; letter-spacing: 0.5px; }
        .ch-dd-loader { padding: 16px; display: flex; justify-content: center; }
        .ch-dd-empty { padding: 14px 12px; text-align: center; font-size: 12px; color: var(--ch-text-3); }
        .ch-dd-list { max-height: 230px; overflow-y: auto; }
        .ch-dd-row {
          width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 12px;
          background: none; border: none; border-bottom: 0.5px solid rgba(0,0,0,0.04);
          cursor: pointer; text-align: left; transition: background 0.12s;
        }
        .ch-dd-row:last-child { border-bottom: none; }
        .ch-dd-row:hover:not(:disabled) { background: var(--ch-surface); }
        .ch-dd-row:disabled { opacity: 0.5; }
        .ch-dd-row--clear { color: var(--ch-text-3); font-size: 12px; }
        .ch-dd-row--danger { color: #dc2626; }
        .ch-dd-row--active { background: #eff6ff; }
        .ch-dd-row-label { flex: 1; font-size: 12.5px; color: var(--ch-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ch-status-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .ch-check-icon { color: var(--ch-green); flex-shrink: 0; }
        .ch-agent-avatar {
          width: 26px; height: 26px; border-radius: var(--ch-radius); flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; color: #fff;
        }
        .ch-agent-info { flex: 1; min-width: 0; }
        .ch-agent-name { display: block; font-size: 12px; font-weight: 600; color: var(--ch-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ch-agent-email { display: block; font-size: 10.5px; color: var(--ch-text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        /* Summary */
        .ch-select {
          width: 100%; padding: 6px 8px; font-size: 12px; border: 0.5px solid var(--ch-border);
          border-radius: var(--ch-radius); outline: none; color: var(--ch-text); background: #fff;
        }
        .ch-select:focus { border-color: var(--ch-green); }
        .ch-generate-btn {
          width: 100%; padding: 7px; background: var(--ch-green); color: #fff; border: none;
          border-radius: var(--ch-radius); font-size: 12.5px; font-weight: 600; cursor: pointer;
          transition: background 0.15s;
        }
        .ch-generate-btn:hover { background: var(--ch-green-dk); }

        /* Mobile bottom sheet (portaled to body) */
        .ch-ms-backdrop {
          position: fixed;
          inset: 0;
          z-index: 200;
          background: rgba(17, 27, 33, 0.45);
          animation: ch-ms-fade 0.22s ease-out;
        }
        @keyframes ch-ms-fade { from { opacity: 0; } to { opacity: 1; } }
        .ch-ms-sheet {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 210;
          background: #fff;
          border-radius: 14px 14px 0 0;
          max-height: min(78vh, 580px);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 -8px 32px rgba(0,0,0,0.18);
          animation: ch-ms-slide 0.28s cubic-bezier(0.32, 0.72, 0, 1);
          padding-bottom: max(12px, env(safe-area-inset-bottom));
        }
        @keyframes ch-ms-slide { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          .ch-ms-backdrop,
          .ch-ms-sheet { animation: none; }
        }
        .ch-ms-handle-wrap {
          display: flex;
          justify-content: center;
          padding: 8px 0 6px;
          flex-shrink: 0;
        }
        .ch-ms-handle {
          width: 36px;
          height: 4px;
          border-radius: 2px;
          background: #d1d5db;
        }
        .ch-ms-toolbar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 10px 8px;
          border-bottom: 0.5px solid var(--ch-border);
          flex-shrink: 0;
        }
        .ch-ms-back {
          width: 38px;
          height: 38px;
          border: none;
          background: transparent;
          border-radius: var(--ch-radius);
          color: var(--ch-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          -webkit-tap-highlight-color: transparent;
        }
        .ch-ms-back:active { background: rgba(0,0,0,0.06); }
        .ch-ms-toolbar-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--ch-text);
        }
        .ch-ms-sheet-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px 6px;
        }
        .ch-ms-sheet-title {
          font-size: 17px;
          font-weight: 700;
          color: var(--ch-text);
        }
        .ch-ms-close-x {
          width: 36px;
          height: 36px;
          border: none;
          background: transparent;
          border-radius: var(--ch-radius);
          color: var(--ch-text-2);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ch-ms-close-x:active { background: rgba(0,0,0,0.06); }
        .ch-ms-root { padding-bottom: 6px; }
        .ch-ms-row {
          width: calc(100% - 24px);
          margin: 0 12px 6px;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border: none;
          border-radius: 10px;
          background: var(--ch-surface);
          color: var(--ch-text);
          font-size: 15px;
          font-weight: 500;
          text-align: left;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }
        .ch-ms-row--toggle {
          justify-content: flex-start;
        }
        .ch-ms-row--toggle .ch-ms-pill {
          margin-left: auto;
        }
        .ch-ms-row:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .ch-ms-row:active:not(:disabled) {
          filter: brightness(0.97);
        }
        .ch-ms-row-icon {
          display: flex;
          color: var(--ch-green-dk);
          flex-shrink: 0;
        }
        .ch-ms-row-text {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ch-ms-row-hint {
          font-size: 12px;
          font-weight: 500;
          color: var(--ch-text-3);
          max-width: 88px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ch-ms-row-chevron { color: var(--ch-text-3); flex-shrink: 0; }
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 700;
          background: rgba(0,0,0,0.06);
          color: var(--ch-text-2);
        }
        .ch-ms-pill--on {
          background: rgba(37,211,102,0.18);
          color: var(--ch-green-dk);
        }
        .ch-ms-scroll {
          overflow-y: auto;
          overscroll-behavior: contain;
          max-height: min(62vh, 480px);
          padding: 4px 0 10px;
        }
        .ch-ms-chip-row {
          width: calc(100% - 24px);
          margin: 0 auto 6px;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border: none;
          border-radius: 10px;
          background: var(--ch-surface);
          text-align: left;
          cursor: pointer;
          font-size: 15px;
          color: var(--ch-text);
        }
        .ch-ms-chip-row--active { background: #ecfdf5; }
        .ch-ms-chip-label { flex: 1; }
        .ch-ms-summary {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .ch-ms-summary-hint {
          margin: 0;
          font-size: 13px;
          color: var(--ch-text-2);
        }
        .ch-ms-agent-row {
          width: calc(100% - 24px);
          margin: 0 auto 6px;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px;
          border: none;
          border-radius: 10px;
          background: var(--ch-surface);
          text-align: left;
          cursor: pointer;
          font-family: inherit;
        }
        .ch-ms-agent-row--active {
          outline: 1.5px solid rgba(37,211,102,0.45);
          background: #ecfdf5;
        }

        .ch-spin { animation: ch-rotate 0.7s linear infinite; }
        @keyframes ch-rotate { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default ChatHeader;